import { Indicator, Root } from "@radix-ui/react-checkbox"
import clsx from "clsx"
import { AnimatePresence, motion } from "framer-motion"
import { forwardRef, useEffect, useId } from "react"
import { useFormContext } from "react-hook-form"
import Skeleton from "react-loading-skeleton"

import { FormControl, FormField, FormItem } from "@molecules/Form/Form"
import { InputName } from "@molecules/Form/inputs/Input/Input.types"

import { Label } from ".."
import styles from "./Checkbox.module.scss"
import { CheckboxProps } from "./Checkbox.types"

const MotionCheckIcon = ({ className }: { className?: string }) => {
    return (
        <AnimatePresence>
            <svg
                className={clsx(className)}
                width="10"
                height="7"
                viewBox="0 0 10 7"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <motion.path
                    d="M1 4L3.5 6L9 1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    initial={{ pathLength: 0 }}
                    animate={{
                        pathLength: 1,
                    }}
                    transition={{ type: "spring", duration: 0.3 }}
                    exit={{ pathLength: 0 }}
                />
            </svg>
        </AnimatePresence>
    )
}

const IndeterminateIcon = ({ className }: { className?: string }) => {
    return (
        <AnimatePresence>
            <svg
                className={clsx(className)}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <motion.path
                    d="M5 8H11"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    initial={{ pathLength: 0 }}
                    animate={{
                        pathLength: 1,
                    }}
                    transition={{ type: "spring", duration: 0.3 }}
                    exit={{ pathLength: 0 }}
                />
            </svg>
        </AnimatePresence>
    )
}

function Checkbox<T extends InputName = InputName>(props: CheckboxProps<T>, ref: React.Ref<HTMLButtonElement>) {
    const {
        name = "",
        id,
        className,

        variant = "default",

        label = "",
        showLabel = false,
        supportText = "",
        showSupportText = false,

        alignment = "left",

        defaultChecked,
        checked,

        isDisabled,
        isLoading = false,
        isError = false,
        isPressed = false,
        isTabbable = true,
        _isFocused = false,
        _isHovered = false,

        isRequired,
        ...rest
    } = props

    const defaultId = useId()

    const renderContent = () => {
        return (
            <div className={styles.contentWrapper}>
                <div className={styles.labelWrapper}>
                    {isLoading ? (
                        <Skeleton
                            width={128}
                            height={10}
                            borderRadius={0}
                            containerClassName={styles[`${alignment}Align`]}
                        />
                    ) : (
                        <Label label={label} isDisabled={isDisabled} />
                    )}
                </div>
                {showSupportText && (
                    <div className={styles.supportTextWrapper}>
                        {isLoading ? (
                            <Skeleton width={272} height={10} borderRadius={0} />
                        ) : (
                            <span className={styles.supportText}>{supportText}</span>
                        )}
                    </div>
                )}
            </div>
        )
    }

    return (
        <Root
            className={clsx(styles.base, className, styles[`${alignment}Align`], styles[variant], {
                [styles.checked]: checked || defaultChecked,
                [styles.loading]: isLoading,
                [styles.error]: isError,
                [styles.disabled]: isDisabled,
                [styles.focused]: _isFocused,
                [styles.hovered]: _isHovered,
                [styles.pressed]: isPressed,
                [styles.withSupportText]: showSupportText,
            })}
            defaultChecked={defaultChecked}
            checked={checked}
            disabled={isDisabled}
            required={isRequired}
            id={id || defaultId}
            tabIndex={isTabbable && !isDisabled && !isLoading ? 0 : -1}
            {...rest}
            name={String(name)}
            ref={ref}
        >
            {alignment === "right" && showLabel && renderContent()}
            <div className={styles.checkboxWrapper}>
                {isLoading ? (
                    <Skeleton borderRadius={4} width={16} height={16} />
                ) : (
                    <div className={styles.checkbox}>
                        <Indicator className={styles.indicator}>
                            {(checked === "indeterminate" || defaultChecked === "indeterminate") && (
                                <IndeterminateIcon />
                            )}
                            {checked !== "indeterminate" && <MotionCheckIcon />}
                        </Indicator>
                    </div>
                )}
            </div>
            {alignment === "left" && showLabel && renderContent()}
        </Root>
    )
}

const withController = <T extends InputName>(CheckboxComponent: React.FC<Omit<CheckboxProps<T>, "isControlled">>) => {
    const Controller = forwardRef<HTMLButtonElement, CheckboxProps<T>>((props, ref) => {
        const { isControlled, displayAsUnchecked, ...rest } = props
        const form = useFormContext()

        if (isControlled) {
            return (
                <FormField
                    control={form.control}
                    name={props.name as string}
                    render={({ field }) => {
                        useEffect(() => {
                            if (props.value !== undefined) {
                                form.setValue(props.name as string, props.value)
                            }
                        }, [props.value])

                        return (
                            <FormItem>
                                <FormControl>
                                    <CheckboxComponent
                                        {...rest}
                                        {...field}
                                        checked={displayAsUnchecked ? false : (field.value as boolean)}
                                        onCheckedChange={field.onChange}
                                        onBlur={field.onBlur}
                                        ref={ref}
                                    />
                                </FormControl>
                            </FormItem>
                        )
                    }}
                />
            )
        } else {
            return <CheckboxComponent {...rest} ref={ref} />
        }
    })

    Controller.displayName = "CheckboxComponentController"

    return Controller as typeof Checkbox
}

// Create the controlled checkbox once at module level to prevent remounting on re-renders
const ControlledCheckbox = withController(forwardRef(Checkbox))

function CheckboxWithController<T extends InputName = InputName>(
    props: CheckboxProps<T>,
    ref: React.Ref<HTMLButtonElement>,
) {
    return <ControlledCheckbox {...props} ref={ref} />
}

const ForwardedCheckbox = forwardRef(CheckboxWithController) as <T extends InputName = InputName>(
    props: CheckboxProps<T>,
    ref: React.Ref<HTMLButtonElement>,
) => React.ReactElement

export default ForwardedCheckbox
